/**
 * "Bundle & save" selector on the product page (rendered inside <bundle-discount-banner>
 * in blocks/buy-buttons.liquid, which site-polish-styles places on its own row above the
 * quantity + Add to cart row).
 *
 * Three options: 1 item (standard price), 2 items (save 10%), 3+ items (save 15%), with
 * live totals for the selected variant. Choosing an option sets the quantity field; changing
 * the quantity field highlights the matching option. The discounts themselves are automatic
 * discounts in Shopify (Buy 2 save 10% / Buy 3 save 15%); this is display only.
 * No motion on hover or selection, only color changes.
 */
const TIERS = [
  { qty: 1, label: '1 item', off: 0, tag: '', note: 'Standard price' },
  { qty: 2, label: '2 items', off: 0.1, tag: 'Most popular', note: '' },
  { qty: 3, label: '3+ items', off: 0.15, tag: 'Best value', note: '' },
];

const STYLE_ID = 'zx-bundle-styles';
const CSS = `
.product-details .product-form-buttons > .bundle-discount-banner.zx-bundle[data-zx] {
  order: -1; flex: 1 0 100%; min-width: 0; display: block;
  padding: 0; border: 0; border-radius: 0; background: none;
  color: #3a2a12; font-weight: 400; transition: none;
}
.zx-bundle__head { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 12px; margin: 0 0 14px; }
.zx-bundle__title { font-family: var(--font-heading--family); font-size: 1rem; font-weight: 700; letter-spacing: 0.01em; color: #3a2a12; }
.zx-bundle__hint { font-size: 0.75rem; color: rgb(58 42 18 / 0.6); white-space: nowrap; }
.zx-bundle__options { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.product-form-buttons .zx-bundle__opt {
  position: relative; display: flex; flex-direction: column; align-items: center; justify-content: flex-start;
  gap: 3px; width: 100%; min-height: 0; margin: 0; padding: 18px 8px 14px;
  border: 1px solid rgb(58 42 18 / 0.16); border-radius: 12px; background: #fffdf8;
  color: #3a2a12; font: inherit; text-align: center; cursor: pointer;
  box-shadow: none; transform: none; transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}
.product-form-buttons .zx-bundle__opt:hover { border-color: rgb(58 42 18 / 0.45); }
.product-form-buttons .zx-bundle__opt[aria-checked='true'] {
  border-color: #3a2a12; background: #fff; box-shadow: 0 0 0 1px #3a2a12, 0 6px 18px -8px rgb(58 42 18 / 0.35);
}
.product-form-buttons .zx-bundle__opt:focus-visible { outline: 2px solid #b47846; outline-offset: 2px; }
.zx-bundle__tag {
  position: absolute; top: 0; left: 50%; transform: translate(-50%, -50%);
  padding: 3px 9px; border-radius: 999px; background: #3a2a12; color: #fdf6e3;
  font-size: 0.625rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; white-space: nowrap;
}
.zx-bundle__radio {
  position: absolute; top: 10px; left: 10px; width: 14px; height: 14px; box-sizing: border-box;
  border: 1.5px solid rgb(58 42 18 / 0.35); border-radius: 50%; background: #fff;
}
.zx-bundle__opt[aria-checked='true'] .zx-bundle__radio { border: 4px solid #3a2a12; }
.zx-bundle__qty { font-size: 0.8125rem; font-weight: 600; letter-spacing: 0.02em; color: rgb(58 42 18 / 0.8); }
.zx-bundle__price { font-size: 1.0625rem; font-weight: 700; line-height: 1.25; color: #3a2a12; }
.zx-bundle__compare { font-size: 0.75rem; color: rgb(58 42 18 / 0.5); text-decoration: line-through; min-height: 1.1em; }
.zx-bundle__save {
  margin-top: 4px; padding: 3px 8px; border-radius: 6px; background: #f2e0ad; color: #8f5f36;
  font-size: 0.6875rem; font-weight: 700; letter-spacing: 0.03em; white-space: nowrap;
}
.zx-bundle__save--plain { background: none; color: rgb(58 42 18 / 0.55); font-weight: 500; }
@media screen and (max-width: 749px) {
  .zx-bundle__options { gap: 8px; }
  .product-form-buttons .zx-bundle__opt { padding: 17px 4px 12px; }
  .zx-bundle__qty { font-size: 0.75rem; }
  .zx-bundle__price { font-size: 0.9375rem; }
  .zx-bundle__save { font-size: 0.625rem; padding: 3px 6px; }
  .zx-bundle__tag { font-size: 0.5625rem; padding: 3px 7px; }
  .zx-bundle__radio { top: 8px; left: 8px; width: 12px; height: 12px; }
  .zx-bundle__opt[aria-checked='true'] .zx-bundle__radio { border-width: 3.5px; }
}
`;

const productCache = new Map();

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'USD';
  const locale = document.documentElement.lang || 'en';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

class BundleDiscountBanner extends HTMLElement {
  connectedCallback() {
    injectStyles();
    this.classList.add('zx-bundle');
    this.dataset.zx = '';
    this.scope = this.closest('.product-form-buttons') ?? this.closest('form') ?? this.parentElement;
    this.form = this.closest('form');
    this.productUrl = this.closest('product-form-component')?.dataset.productUrl;
    this.build();

    this.update = () => this.sync();
    this.onVariant = () => this.refreshPrices();
    this.scope?.addEventListener('input', this.update);
    this.scope?.addEventListener('change', this.update);
    this.scope?.addEventListener('quantity-selector:update', this.update);
    document.addEventListener('cart:update', this.update);
    document.addEventListener('variant:update', this.onVariant);
    this.sync();
    this.refreshPrices();
  }

  disconnectedCallback() {
    this.scope?.removeEventListener('input', this.update);
    this.scope?.removeEventListener('change', this.update);
    this.scope?.removeEventListener('quantity-selector:update', this.update);
    document.removeEventListener('cart:update', this.update);
    document.removeEventListener('variant:update', this.onVariant);
  }

  build() {
    const options = TIERS.map(
      (tier) => `
        <button type="button" class="zx-bundle__opt" role="radio" aria-checked="false" data-qty="${tier.qty}">
          ${tier.tag ? `<span class="zx-bundle__tag">${tier.tag}</span>` : ''}
          <span class="zx-bundle__radio" aria-hidden="true"></span>
          <span class="zx-bundle__qty">${tier.label}</span>
          <span class="zx-bundle__price" data-price></span>
          <span class="zx-bundle__compare" data-compare></span>
          ${
            tier.off
              ? `<span class="zx-bundle__save">Save ${Math.round(tier.off * 100)}%</span>`
              : `<span class="zx-bundle__save zx-bundle__save--plain">${tier.note}</span>`
          }
        </button>`
    ).join('');

    this.innerHTML = `
      <div class="zx-bundle__head">
        <span class="zx-bundle__title">Bundle &amp; save</span>
        <span class="zx-bundle__hint">Discount applied at checkout</span>
      </div>
      <div class="zx-bundle__options" role="radiogroup" aria-label="Bundle and save">${options}</div>`;

    this.buttons = [...this.querySelectorAll('.zx-bundle__opt')];
    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.choose(Number(button.dataset.qty)));
    });
  }

  get quantityInput() {
    return this.scope?.querySelector('input[name="quantity"]');
  }

  get quantity() {
    const value = parseInt(this.quantityInput?.value ?? '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  choose(qty) {
    const input = this.quantityInput;
    if (input) {
      input.value = String(qty);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.sync(qty);
  }

  sync(forced) {
    const quantity = forced ?? this.quantity;
    const tier = quantity >= 3 ? 2 : quantity >= 2 ? 1 : 0;
    this.dataset.tier = String(tier);
    this.buttons?.forEach((button, index) => {
      button.setAttribute('aria-checked', String(index === tier));
      button.tabIndex = index === tier ? 0 : -1;
    });
  }

  async refreshPrices() {
    if (!this.productUrl) return;
    try {
      if (!productCache.has(this.productUrl)) {
        productCache.set(
          this.productUrl,
          fetch(`${this.productUrl.split('?')[0]}.js`).then((response) => response.json())
        );
      }
      const product = await productCache.get(this.productUrl);
      const variantId = Number(this.form?.querySelector('input[name="id"]')?.value);
      const variant = product.variants.find((item) => item.id === variantId) ?? product.variants[0];
      if (!variant) return;
      this.buttons.forEach((button, index) => {
        const tier = TIERS[index];
        const full = variant.price * tier.qty;
        const discounted = Math.round(full * (1 - tier.off));
        button.querySelector('[data-price]').textContent = formatMoney(discounted);
        button.querySelector('[data-compare]').textContent = tier.off ? formatMoney(full) : '';
      });
    } catch {
      /* Prices are a bonus; the options still work without them. */
    }
  }
}

if (!customElements.get('bundle-discount-banner')) {
  customElements.define('bundle-discount-banner', BundleDiscountBanner);
}
